import { ActionType, ProTable } from "@ant-design/pro-components";
import { useGlobalState } from "../../components/global";
import { useEffect, useRef } from "react";
import { useRequest } from "ahooks";
import UUIDComponent from "../../components/UUIDComponent";
import { deviceType, payStatus, payType, vipType } from "../const";
import { Button, Form, message, Modal, Select, Tag, Tooltip } from "antd";
import { Refund } from "../components/Refund";

const isAppleOrder = (record: any) =>
    record?.payType === "APPLE" || record?.payType === "APPLE_AUTO";

const formatMoney = (value: unknown, unit = "元") => {
    const amount = Number(value);
    return `${(Number.isFinite(amount) ? amount / 100 : 0).toFixed(2)} ${unit}`;
};

const refundableAmount = (record: any) => {
    const value = Number(record?.refundableAmount);
    if (Number.isFinite(value)) {
        return Math.max(value, 0);
    }
    return Math.max(Number(record?.payAmount || 0) - Number(record?.refundedAmount || 0), 0);
};

export default function MemberOrder() {
    const { jsonRequest } = useGlobalState();
    const tableRef = useRef<ActionType>();
    const [form] = Form.useForm();

    useEffect(() => {
        tableRef.current?.reload();
    }, []);

    const columns: any[] = [
        {
            title: "ID",
            dataIndex: "id",
            align: "center",
            search: false,
            width: 50,
            render: (value: any) => <UUIDComponent value={value} />
        },
        {
            title: "用户id",
            dataIndex: "userId",
            align: "center",
            search: true,
            width: 100,
            render: (value: any) => <UUIDComponent value={value} />
        },
        {
            title: "购买内容",
            dataIndex: "vipPkgTitle",
            align: "center",
            search: false,
            width: 100,
        },
        {
            title: "会员类型",
            dataIndex: "vipType",
            align: "center",
            search: false,
            width: 110,
            valueEnum: vipType,
        },
        {
            title: "备注",
            dataIndex: "remark",
            align: "center",
            search: false,
            width: 100,
        },
        {
            title: "国家地区",
            dataIndex: "payRealCountry",
            align: "center",
            search: false,
            width: 100,
        },
        {
            title: "支付金额",
            dataIndex: "payAmount",
            align: "center",
            search: false,
            render: (text: number, record: any) => formatMoney(text, record.payRealCurrency || "元"),
            width: 100,
        },
        {
            title: "总金额",
            dataIndex: "totalAmount",
            align: "center",
            search: false,
            width: 100,
            render: (text: number) => formatMoney(text),
        },
        {
            title: "优惠金额",
            dataIndex: "reductionAmount",
            align: "center",
            search: false,
            width: 100,
            render: (text: number) => formatMoney(text),
        },
        {
            title: "订单号",
            dataIndex: "orderNo",
            align: "center",
            search: true,
            width: 100,
            render: (value: any) => <UUIDComponent value={value} flag={true} />
        },
        {
            title: "第三方订单号",
            dataIndex: "mchOrderNo",
            align: "center",
            search: false,
            width: 100,
            render: (value: any) => <UUIDComponent value={value} flag={true} />
        },
        {
            title: "渠道",
            dataIndex: "channel",
            align: "center",
            search: false,
            width: 100,
        },
        {
            title: "设备类型",
            dataIndex: "deviceType",
            align: "center",
            search: false,
            width: 100,
            valueEnum: deviceType,
        },
        {
            title: "支付方式",
            dataIndex: "payType",
            align: "center",
            search: true,
            width: 100,
            valueEnum: payType,
        },
        {
            title: "自动续订",
            dataIndex: "subscriptionAutoRenew",
            align: "center",
            search: false,
            width: 110,
            render: (value: boolean | null, record: any) => {
                if (record.payType !== "APPLE_AUTO") {
                    return "-";
                }
                if (value === true) {
                    return <Tag color="success">续订中</Tag>;
                }
                if (value === false) {
                    return <Tag color="warning">已关闭</Tag>;
                }
                return <Tag>待同步</Tag>;
            },
        },
        {
            title: "订阅协议号",
            dataIndex: "subscriptionAgreementNo",
            align: "center",
            search: false,
            width: 150,
            render: (value: any) => value
                ? <UUIDComponent value={value} flag={true} />
                : "-",
        },
        {
            title: "权益周期",
            key: "effectivePeriod",
            align: "center",
            search: false,
            width: 190,
            render: (_: any, record: any) => {
                if (record.vipType === "PERMANENT") {
                    return "永久";
                }
                if (!record.effectiveDateBegin && !record.effectiveDateEnd) {
                    return "-";
                }
                return `${record.effectiveDateBegin || "-"} 至 ${record.effectiveDateEnd || "-"}`;
            },
        },
        {
            title: "是否支付",
            dataIndex: "payFlag",
            align: "center",
            search: true,
            width: 100,
            render: (text: boolean) => text ? '是' : '否',
            renderFormItem: () => (
                <Select
                    allowClear
                    placeholder='请选择是否支付'
                    options={[
                        { label: "是", value: true },
                        { label: "否", value: false },
                    ]}
                />
            ),
        },
        {
            title: "已退金额",
            dataIndex: "refundedAmount",
            align: "center",
            search: false,
            width: 100,
            render: (text: number, record: any) => formatMoney(text, record.payRealCurrency || "元"),
        },
        {
            title: "可退金额",
            dataIndex: "refundableAmount",
            align: "center",
            search: false,
            width: 100,
            render: (_: number, record: any) =>
                formatMoney(refundableAmount(record), record.payRealCurrency || "元"),
        },
        {
            title: "订单状态",
            dataIndex: "status",
            align: "center",
            search: true,
            width: 100,
            valueEnum: payStatus,
        },
        {
            title: "下单时间",
            dataIndex: "orderedTime",
            align: "center",
            search: false,
            width: 100,
        },
        {
            title: "支付时间",
            dataIndex: "payTime",
            align: "center",
            search: false,
            width: 100,
        },
        {
            title: "下单时间",
            dataIndex: "orderedTime",
            align: "center",
            valueType: "dateRange",
            hideInTable: true,
        },
        {
            title: "操作",
            key: 'operation',
            search: false,
            align: "center",
            fixed: 'right',
            width: 120,
            render: (_: any, record: any) => {
                if (record.status !== "PAID" || refundableAmount(record) <= 0) {
                    return "-";
                }
                if (isAppleOrder(record)) {
                    return (
                        <Tooltip title="苹果订单需在 App Store Connect 发起退款，结果由苹果回调同步">
                            <Tag color="processing">App Store 处理</Tag>
                        </Tooltip>
                    );
                }
                return (
                    <Button type="link" danger onClick={() => openRefund(record)}>
                            退款
                    </Button>
                );
            },
        }
    ];

    const [model, contextHolder] = Modal.useModal();

    // 退款
    const submitRefund = useRequest(
        (params: any) =>
            jsonRequest.post("/back/refund-record/add", {
                pwd: params.pwd,
                orderNo: params.orderNo,
                totalAmount: Math.round(Number(params.totalAmount) * 100),
                businessType: params.businessType,
                realRefundFlag: params.realRefundFlag,
                reason: params.reason,
            }),
        {
            loadingDelay: 500,
            manual: true,
            onError: (e: any) => message.error(e?.message || "退款失败"),
        },
    );

    function openRefund(record: any) {
        model.warning({
            width: 600,
            title: "退款确认",
            content: <Refund form={form} record={record} amount={refundableAmount(record) / 100} />,
            closable: true,
            okText: "提交",
            okButtonProps: { loading: submitRefund.loading },
            async onOk() {
                const values = await form.validateFields();
                console.log(values);
                const submit: any = await submitRefund.runAsync({
                    ...values,
                });
                if (submit?.body?.code === -1) {
                    message.error(submit?.body?.msg || "退款失败");
                    return;
                }
                form.resetFields();
                message.success("退款申请成功");
                tableRef.current?.reload();
            },
        });
    }

    const request = async (params: any) => {
        const result: any = await jsonRequest.get("/back/vip-order/page", {
            currentPage: params.current,
            pageSize: params.pageSize,
            userId: params?.userId,
            orderNo: params?.orderNo,
            payType: params?.payType,
            payFlag: params?.payFlag,
            status: params?.status,
            queryBeginDate: params?.orderedTime?.[0],
            queryEndDate: params?.orderedTime?.[1],
        }).then((resp: any) => resp.json());
        const { data } = result;
        return { data: data?.data, success: result.code === 0, total: data?.count };
    };

    return <>
        <h1>会员订单</h1>
        <ProTable
            rowKey="id"
            scroll={{ x: 2500 }}
            actionRef={tableRef}
            cardBordered
            columns={columns}
            request={request}
            pagination={{ defaultPageSize: 10, showSizeChanger: true }}
            search={{ span: 6, defaultCollapsed: false }}
        />
        {contextHolder}
    </>;
}
